import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  ClockMode,
  ClockPeriod,
  CLOCK_MINUTE_STEP,
  formatClockTime,
  handDegrees,
  hourWithPeriod,
  parseClockTime,
  snapClockMinute,
  timeFromDialPoint,
} from '../../utils/clockTime';
import './ClockTimeField.css';

const HOURS = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTE_MARKS = [0, 15, 30, 45];

interface ClockTimeFieldProps {
  /** HH:MM in 24-hour time, or '' when no time is chosen. */
  value: string;
  onChange: (value: string) => void;
  /** Visible label above the field. */
  label?: string;
  /** Accessible name when a visible label is rendered by the parent. */
  ariaLabel?: string;
  id?: string;
  className?: string;
  /** Open the clock as soon as the field appears. */
  autoOpen?: boolean;
  disabled?: boolean;
  /** Called when the clock closes, so a parent can leave edit mode. */
  onDismiss?: () => void;
}

interface PopoverBox {
  left: number;
  top?: number;
  bottom?: number;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function slotStyle(index: number, count: number): React.CSSProperties {
  const angle = ((index * (360 / count)) - 90) * (Math.PI / 180);
  const radius = 36;
  return {
    left: `${50 + radius * Math.cos(angle)}%`,
    top: `${50 + radius * Math.sin(angle)}%`,
  };
}

const ClockTicks = () => (
  <svg className="clock-ticks" viewBox="0 0 100 100" aria-hidden="true">
    {Array.from({ length: 60 }, (_, index) => {
      const angle = ((index * 6) - 90) * (Math.PI / 180);
      const major = index % CLOCK_MINUTE_STEP === 0;
      const outer = 47.5;
      const inner = major ? 43.2 : 45.4;
      return (
        <line
          key={index}
          x1={50 + inner * Math.cos(angle)}
          y1={50 + inner * Math.sin(angle)}
          x2={50 + outer * Math.cos(angle)}
          y2={50 + outer * Math.sin(angle)}
          stroke={major ? '#c6a15b' : '#e4d8c4'}
          strokeWidth={major ? 0.7 : 0.35}
        />
      );
    })}
  </svg>
);

/**
 * A clock face for choosing a shoot time. The browser's own time box
 * opens as scrolling columns, so this keeps the round clock.
 */
const ClockTimeField = ({
  value,
  onChange,
  label,
  ariaLabel,
  id,
  className = '',
  autoOpen = false,
  disabled = false,
  onDismiss,
}: ClockTimeFieldProps) => {
  const generatedId = useId();
  const fieldId = id || generatedId;
  const parsed = parseClockTime(value);
  const hour = parsed ? parsed.hour : null;
  const minute = parsed ? parsed.minute : null;
  const [open, setOpen] = useState(autoOpen);
  const [mode, setMode] = useState<ClockMode>('hour');
  const [period, setPeriod] = useState<ClockPeriod>(hour != null && hour >= 12 ? 'pm' : 'am');
  const [box, setBox] = useState<PopoverBox | null>(null);
  const [dragging, setDragging] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const dialRef = useRef<HTMLDivElement>(null);
  const dragged = useRef(false);
  const name = ariaLabel || label || 'Time';

  useEffect(() => {
    if (hour == null) return;
    setPeriod(hour >= 12 ? 'pm' : 'am');
  }, [hour]);

  const place = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 300;
    const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8));
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < 420 && rect.top > spaceBelow) {
      setBox({ left, bottom: window.innerHeight - rect.top + 8 });
    } else {
      setBox({ left, top: rect.bottom + 8 });
    }
  };

  useLayoutEffect(() => {
    if (!open) return;
    place();
    const hourButton = popRef.current?.querySelector<HTMLButtonElement>('[data-clock="hour"]');
    hourButton?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPlace = () => place();
    const onDoc = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target) || popRef.current?.contains(target)) return;
      setOpen(false);
      onDismiss?.();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      onDismiss?.();
      triggerRef.current?.focus();
    };
    window.addEventListener('resize', onPlace);
    window.addEventListener('scroll', onPlace, true);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', onPlace);
      window.removeEventListener('scroll', onPlace, true);
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onDismiss]);

  const commit = (nextHour: number, nextMinute: number) => {
    onChange(formatClockTime(nextHour, nextMinute));
  };

  const applyPoint = (clientX: number, clientY: number, nextMode: ClockMode) => {
    const rect = dialRef.current?.getBoundingClientRect();
    if (!rect) return;
    const picked = timeFromDialPoint(
      clientX - (rect.left + rect.width / 2),
      clientY - (rect.top + rect.height / 2),
      nextMode,
      period,
    );
    if (nextMode === 'hour') commit(picked, minute ?? 0);
    else commit(hour ?? (period === 'pm' ? 12 : 0), picked);
  };

  const onDialPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    dragged.current = false;
    setDragging(false);
    event.currentTarget.setPointerCapture(event.pointerId);
    applyPoint(event.clientX, event.clientY, mode);
  };

  const onDialPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    if (!dragged.current) setDragging(true);
    dragged.current = true;
    applyPoint(event.clientX, event.clientY, mode);
  };

  const onDialPointerUp = () => {
    const wasDrag = dragged.current;
    setDragging(false);
    if (mode === 'hour' && wasDrag) setMode('minute');
  };

  const pickMark = (mark: number) => {
    if (dragged.current) return;
    if (mode === 'hour') {
      const next = period === 'am' ? (mark === 12 ? 0 : mark) : (mark === 12 ? 12 : mark + 12);
      commit(next, minute ?? 0);
      setMode('minute');
      return;
    }
    commit(hour ?? (period === 'pm' ? 12 : 0), mark);
  };

  const choosePeriod = (next: ClockPeriod) => {
    setPeriod(next);
    if (hour == null) return;
    commit(hourWithPeriod(hour, next), minute ?? 0);
  };

  const onPopoverKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.clock-readout') && !target.closest('.clock-dial')) return;
    const delta = event.key === 'ArrowUp' || event.key === 'ArrowRight'
      ? 1
      : event.key === 'ArrowDown' || event.key === 'ArrowLeft'
        ? -1
        : 0;
    if (!delta) return;
    event.preventDefault();
    if (mode === 'hour') {
      const base = hour ?? (period === 'pm' ? 12 : 0);
      commit((base + delta + 24) % 24, minute ?? 0);
      return;
    }
    const baseMinute = snapClockMinute(minute ?? 0);
    const nextMinute = (baseMinute + (delta * CLOCK_MINUTE_STEP) + 60) % 60;
    commit(hour ?? (period === 'pm' ? 12 : 0), nextMinute);
  };

  const close = () => {
    setOpen(false);
    onDismiss?.();
    triggerRef.current?.focus();
  };

  const selectedHourFace = hour == null ? null : (hour % 12 === 0 ? 12 : hour % 12);
  const degrees = hour == null ? 0 : handDegrees(mode, hour, minute ?? 0);
  const marks = mode === 'hour' ? HOURS : MINUTE_MARKS;

  const popover = open ? (
    <div
      ref={popRef}
      className="clock-popover"
      role="dialog"
      aria-label={`Choose ${name.toLowerCase()}`}
      style={box ? { left: box.left, top: box.top, bottom: box.bottom } : { left: -9999, top: 0 }}
      onKeyDown={onPopoverKeyDown}
    >
      <div className="clock-readout">
        <button
          type="button"
          data-clock="hour"
          className={mode === 'hour' ? 'is-active' : ''}
          aria-pressed={mode === 'hour'}
          onClick={() => setMode('hour')}
        >
          {hour == null ? '––' : pad(hour)}
          <span>Hour</span>
        </button>
        <span className="clock-colon" aria-hidden="true">:</span>
        <button
          type="button"
          className={mode === 'minute' ? 'is-active' : ''}
          aria-pressed={mode === 'minute'}
          onClick={() => setMode('minute')}
        >
          {minute == null ? '––' : pad(minute)}
          <span>Minute</span>
        </button>
      </div>
      <div className="clock-period" role="group" aria-label="AM or PM">
        <button type="button" aria-pressed={period === 'am'} className={period === 'am' ? 'is-selected' : ''} onClick={() => choosePeriod('am')}>AM</button>
        <button type="button" aria-pressed={period === 'pm'} className={period === 'pm' ? 'is-selected' : ''} onClick={() => choosePeriod('pm')}>PM</button>
      </div>
      <div
        ref={dialRef}
        className={`clock-dial${dragging ? ' is-dragging' : ''}`}
        onPointerDown={onDialPointerDown}
        onPointerMove={onDialPointerMove}
        onPointerUp={onDialPointerUp}
      >
        <ClockTicks />
        {hour != null ? (
          <div className="clock-hand" style={{ transform: `rotate(${degrees}deg)` }} />
        ) : null}
        <div className="clock-hub" />
        {marks.map((mark, index) => {
          const selected = mode === 'hour' ? mark === selectedHourFace : minute === mark;
          const text = mode === 'hour' ? String(mark) : pad(mark);
          return (
            <button
              key={`${mode}-${text}`}
              type="button"
              className={selected ? 'is-selected' : ''}
              style={slotStyle(index, marks.length)}
              aria-label={mode === 'hour' ? `${mark} o'clock` : `${mark} minutes`}
              onClick={() => pickMark(mark)}
            >
              {text}
            </button>
          );
        })}
      </div>
      <p className="clock-hint">
        {mode === 'hour' ? 'Turn the hand to the hour. It then asks for the minutes.' : 'Times sit on the quarter hour.'}
      </p>
      <div className="clock-actions">
        <button
          type="button"
          className="clock-clear"
          onClick={() => {
            onChange('');
            setMode('hour');
          }}
          disabled={!value}
        >
          Clear
        </button>
        <button type="button" className="clock-done" onClick={close}>Done</button>
      </div>
    </div>
  ) : null;

  return (
    <div ref={rootRef} className={`clock-time ${className}`.trim()}>
      {label ? <label htmlFor={fieldId}>{label}</label> : null}
      <button
        ref={triggerRef}
        id={fieldId}
        type="button"
        className="clock-time-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={value ? `${name}, ${value}` : name}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setMode('hour');
          if (open) onDismiss?.();
          setOpen((current) => !current);
        }}
      >
        <span>{value || 'Set a time'}</span>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M12 8.5V12l2.5 1.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
      {popover && typeof document !== 'undefined' ? createPortal(popover, document.body) : null}
    </div>
  );
};

export default ClockTimeField;
